import {
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import {
  catchError,
  defer,
  from,
  Observable,
  switchMap,
  throwError,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthSessionService } from '../services/auth-session.service';
import { SessionExpiredError } from '../services/session-expired.error';

export const AuthInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  if (!isConfiguredApiRequest(request.url)) {
    return next(request);
  }

  const authSessionService = inject(AuthSessionService);
  return defer(() => from(authSessionService.getCurrentToken())).pipe(
    switchMap((token) =>
      next(
        request.clone({
          headers: request.headers.set('Authorization', `Bearer ${token}`),
        }),
      ),
    ),
    catchError((error: unknown) => {
      if (error instanceof SessionExpiredError) {
        return throwError(() => error);
      }

      if (isUnauthorized(error)) {
        return from(authSessionService.clearSession()).pipe(
          switchMap(() =>
            throwError(
              () =>
                new SessionExpiredError(
                  'La sessione è scaduta. Accedi di nuovo per proseguire.',
                  error,
                ),
            ),
          ),
        );
      }

      return throwError(() => error);
    }),
  );
};

function isConfiguredApiRequest(requestUrl: string): boolean {
  const apiUrl = new URL(environment.apiUrl);
  const request = new URL(requestUrl, apiUrl.origin);
  const apiPath = apiUrl.pathname.replace(/\/$/, '');

  return (
    request.origin === apiUrl.origin &&
    (request.pathname === apiPath || request.pathname.startsWith(`${apiPath}/`))
  );
}

function isUnauthorized(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    error.status === 401
  );
}
